import { getSingleValueForTasField } from '../../utils/tuition-assistance/fields';
import { reportSeveredLink } from '../../utils/severed-link-report';
import type { TasCourse } from './tas-course.type.ts';
export interface TasCourseExtension extends TasCourse {}
export class TasCourseExtension {
  /**************************
   **  Computed Properties **
   **************************/
  get approvalAssets() {
    return Object.freeze(
      this.tasAssets.filter(
        (asset) =>
          !asset.attachmentDeletedAt && asset.classification === 'COURSE_APPROVAL_DOCUMENTS'
      )
    );
  }

  get evidenceAssets() {
    return Object.freeze(
      this.tasAssets.filter(
        (asset) => !asset.attachmentDeletedAt && asset.classification === 'COMPLETION_DOCUMENTS'
      )
    );
  }

  get courseDescription() {
    return this.fields?.['COURSE_DESCRIPTION']?.values?.[0] || '';
  }

  get courseCredit() {
    return this.fields?.['COURSE_CREDIT']?.values?.[0] || 0;
  }

  get courseName() {
    return this.fields?.['COURSE_NAME']?.values?.[0] || '';
  }

  get courseNumber() {
    return this.fields?.['COURSE_NUMBER']?.values?.[0] || '';
  }

  get courseGrade() {
    return this.fields?.['COURSE_GRADE']?.values?.[0] || '';
  }

  get reimbursementTotalByCredit() {
    const rate = this.tasApplication?.tasProgramInstance?.tasProgramTemplate?.ratePerCredit;
    const credits = getSingleValueForTasField('COURSE_CREDIT', this.fields) as number;
    if (!rate || !credits) {
      return 0;
    }
    return rate * credits;
  }

  get passingCredit(): number {
    if (!this.hasGrades) {
      return 0;
    }
    const passingGrades =
      this.tasApplication?.tasProgramInstance?.tasProgramTemplate?.passingGrades || [];
    if (passingGrades.includes(this.courseGrade.trim())) {
      return (getSingleValueForTasField('COURSE_CREDIT', this.fields) as number) || 0;
    }
    return 0;
  }

  get courseTotal() {
    const programInstance = this.tasApplication?.tasProgramInstance;
    if (!programInstance) {
      reportSeveredLink({
        observer: 'tas-course',
        edge: 'tasApplication.tasProgramInstance',
        recordId: this.id ?? undefined,
      });
      // Without the template config the non-credit expense sum is the only safe total.
      return this.courseExpenses;
    }
    const calculateTotalByCreditCredit = programInstance.tasProgramTemplate?.calculateTotalByCredit;
    return calculateTotalByCreditCredit ? this.reimbursementTotalByCredit : this.courseExpenses;
  }

  get courseExpenses() {
    const fields = this.fields;

    if (!fields) {
      return 0;
    }

    const costs = [
      this.courseTuition,
      this.courseBooks,
      this.courseLabs,
      this.courseSoftware,
      this.courseFees,
    ];

    const reduced = costs.reduce((a: number, b: number) => {
      return a + Number(b);
    }, 0);

    return reduced;
  }

  get courseTuition() {
    return this.fields?.['COURSE_TUITION']?.values?.[0] || 0;
  }

  get courseBooks() {
    return this.fields?.['COURSE_BOOKS']?.values?.[0] || 0;
  }

  get courseLabs() {
    return this.fields?.['COURSE_LABS']?.values?.[0] || 0;
  }

  get courseSoftware() {
    return this.fields?.['COURSE_SOFTWARE']?.values?.[0] || 0;
  }

  get courseFees() {
    return this.fields?.['COURSE_FEES']?.values?.[0] || 0;
  }

  get nonTuitionExpenses() {
    return this.courseExpenses - this.courseTuition;
  }

  get displayName() {
    return [this.courseNumber, this.courseName]
      .filter((val) => val !== undefined && val !== null)
      .join(' - ');
  }

  get hasGrades() {
    return !!getSingleValueForTasField('COURSE_GRADE', this.fields);
  }

  get hasGradeWhenRequired() {
    const gradeRequired =
      this.tasApplication?.tasProgramInstance?.tasProgramTemplate?.courseGradeRequired;
    return gradeRequired ? this.hasGrades : true;
  }

  get hasRequiredEvidence() {
    return !!this.evidenceAssets.length;
  }
}

const Registration = {
  kind: 'object',
  name: 'tas-course',
  features: TasCourseExtension,
};
export default Registration;
